import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardCheck, ArrowUpRight, AlertCircle, Lock, Loader2, Clock,
} from "lucide-react";

/**
 * Inspection Recommendation Card — live preview of the downstream workflow.
 *
 * Once intake is complete, the "Send to workshop" button hands off to the
 * normal FOH Request VI / Diagnostic screen (`/app/foh/intake`) with every
 * captured field pre-filled in the URL. The FOH does the final review there
 * (mileage, key tag, GH ref) and submits. On submit, the resulting job is
 * back-linked to this intake session via the `intake_session_id` URL param.
 *
 * MOT-only carve-out (May 2026): when `scope_kind === "mot_only_test"`, we
 * skip the VI request page entirely. We render an inline slot-time + duration
 * strip right next to the CTA — these two fields drive the Stage 2 background
 * DVSA poller, so capturing them on handoff is critical (and burying them at
 * the bottom of the long RECAP page caused the FOH to miss them). On click
 * we POST directly to `/foh/intake/send-to-workshop` (no VI flags) and
 * navigate to the MOT workspace.
 */
export default function InspectionRecommendationCard({
  session,
  recommendation,
  isLocked,
  onLinked,
}) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // MOT-only slot-time + duration — fetched from booking_intent on mount.
  // These are the SAME values the (lower-down) MOTBookingIntentCard binds
  // to; we just surface them here too so the FOH can see + edit them right
  // beside the "Send to workshop" CTA. Edits POST back to the same
  // booking-intent endpoint, which mirrors into mot_workflow.mot_slot_time.
  const [slotTime, setSlotTime] = useState("");
  const [duration, setDuration] = useState("");
  const [slotLoading, setSlotLoading] = useState(false);

  const isMotOnlyTest = recommendation?.scope_kind === "mot_only_test";

  // Lazy-load booking_intent so we know the current slot. Best-effort: any
  // failure leaves the inputs blank, which is harmless.
  useEffect(() => {
    if (!isMotOnlyTest || !session?.id) return;
    let cancelled = false;
    setSlotLoading(true);
    axios
      .get(`${API}/intake/sessions/${session.id}/booking-intent`)
      .then((res) => {
        if (cancelled) return;
        const bi = res.data?.booking_intent || {};
        // Convert ISO → datetime-local (local tz YYYY-MM-DDTHH:MM)
        const iso = bi.mot_slot_time;
        if (iso) {
          try {
            const d = new Date(iso);
            const p = (n) => String(n).padStart(2, "0");
            setSlotTime(
              `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`,
            );
          } catch {
            setSlotTime("");
          }
        }
        if (bi.expected_duration_minutes) setDuration(bi.expected_duration_minutes);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSlotLoading(false);
      });
    return () => { cancelled = true; };
  }, [isMotOnlyTest, session?.id]);

  const persistSlot = useCallback(
    async (patch) => {
      if (!isMotOnlyTest || !session?.id) return;
      try {
        await axios.post(
          `${API}/intake/sessions/${session.id}/booking-intent`,
          patch,
        );
      } catch (_e) {
        // Silent — the user will see the validation error on Send if the
        // value is invalid.
      }
    },
    [isMotOnlyTest, session?.id],
  );

  if (!recommendation) return null;

  const label = recommendation.inspection_type_label || "Inspection";
  const hereFor = recommendation.vehicle_here_for;
  const linkedJobId = session?.linked_job_id;

  // iter199ah — Multi-service add-on selected at the triage leaf.
  // When the FOH ticked Service or Diag alongside MOT in the
  // wizard, we MUST NOT take the MOT-only fast path — it would
  // drop the tech allocation. Instead, go through the workshop
  // intake form with all three checkboxes pre-ticked.
  const servicesRequested = Array.isArray(session?.services_requested)
    ? session.services_requested.map((s) => (s || "").toLowerCase())
    : [];
  const wantsMot     = servicesRequested.includes("mot");
  const wantsService = servicesRequested.includes("service");
  const wantsDiag    = servicesRequested.includes("diag");
  const isMultiService = servicesRequested.length > 1;

  // MOT-only direct handoff — skip the VI request form entirely.
  const handoffMotOnly = async () => {
    setSubmitError(null);
    // Validate slot fields before we commit — these drive the Stage 2
    // background poller, so missing them silently would defeat the
    // whole "Zero Tester Admin" loop.
    if (!slotTime) {
      setSubmitError("Set the MOT slot time before sending to the workshop.");
      return;
    }
    const dur = Number(duration);
    if (!dur || dur < 5 || dur > 480) {
      setSubmitError("Set the expected duration (5–480 minutes).");
      return;
    }
    setSubmitting(true);
    try {
      // Make sure the booking_intent persists the latest slot before
      // we cut the job — booking_intent mirrors into mot_workflow.
      const iso = new Date(slotTime).toISOString();
      await persistSlot({
        mot_slot_time: iso,
        expected_duration_minutes: dur,
      });

      const payload = {
        reg: (session?.vehicle_reg || "").toUpperCase(),
        make: session?.make || "",
        model: session?.model || "",
        requires_diagnostic_vi: false,
        requires_vehicle_inspection: false,
        customer_concern: "",
        vehicle_on_site: true,
        key_tag_number: "",
        gh_job_ref: "",
        vehicle_here_for: hereFor || "mot",
        colour: session?.colour || "",
        fuel_type: session?.fuel_type || "",
        year: session?.year || null,
        engine_capacity: null,
        tax_status: "",
        mot_status: "",
        transmission: "",
        customer_name: session?.customer_name || "",
        customer_phone: session?.contact_number || "",
        customer_email: session?.customer_email || "",
        intake_session_id: session?.id || null,
      };
      const res = await axios.post(`${API}/foh/intake/send-to-workshop`, payload);
      const jobId = res.data?.id || res.data?.job?.id || res.data?.job_id;
      if (jobId) {
        if (onLinked) onLinked(jobId);
        // Send the FOH straight to the MOT workspace where slot time,
        // DVSA auto-pull, and pricing bridge controls live.
        navigate(`/app/tech/mot/${jobId}`);
      } else {
        setSubmitError("Job created, but no job id returned. Refresh and find it on the dashboard.");
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Could not create MOT job — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handoff = () => {
    // iter199ah — Multi-service intake (MOT + Service/Diag in any
    // combination) MUST go through the workshop form so the new MOT
    // Test checkbox is visible/editable and the tech allocation is
    // created. Only pure MOT-only takes the direct fast path.
    if (isMotOnlyTest && !isMultiService) {
      handoffMotOnly();
      return;
    }

    // Derive the form's pre-tick state from services_requested
    // (preferred) and fall back to the recommendation engine flags
    // for legacy sessions that pre-date iter199ah.
    const requiresDiag = wantsDiag    || !!recommendation.requires_diagnostic_vi;
    const requiresVi   = wantsService || !!recommendation.requires_vehicle_inspection;
    const requiresMot  = wantsMot     || isMotOnlyTest;

    // Build the customer concern string from triage tech-pack lines + foh notes.
    // Server already populates this in create-inspection-request preview; we
    // mirror the same logic on the FE for the hand-off URL.
    const concern = (
      (session?.foh_notes || "").trim()
      || (Array.isArray(session?.triage_path_labels)
            ? session.triage_path_labels.join(" → ")
            : "")
      || ""
    );

    const params = new URLSearchParams();
    if (session?.vehicle_reg) params.set("reg", session.vehicle_reg);
    if (session?.customer_name) params.set("customername", session.customer_name);
    if (session?.contact_number) params.set("customerphone", session.contact_number);
    if (session?.customer_email) params.set("customeremail", session.customer_email);
    if (session?.make) params.set("make", session.make);
    if (session?.model) params.set("vehiclemodel", session.model);
    if (session?.year) params.set("year", String(session.year));
    if (session?.colour) params.set("colour", session.colour);
    if (session?.fuel_type) params.set("fueltype", session.fuel_type);
    if (requiresDiag) params.set("diag", "1");
    if (requiresVi) params.set("vi", "1");
    // iter199ah — Surface MOT Test selection to the workshop form.
    if (requiresMot) params.set("mot", "1");
    if (concern) params.set("concern", concern);
    if (hereFor) params.set("hereFor", hereFor);
    if (session?.id) params.set("intake_session_id", session.id);

    navigate(`/app/foh/intake?${params.toString()}`);
  };

  return (
    <section
      className={`rounded-lg border ${
        isMotOnlyTest
          ? "border-slate-200 bg-gradient-to-br from-slate-50/70 to-white"
          : "border-teal-200 bg-gradient-to-br from-teal-50/60 to-white"
      }`}
      data-testid="inspection-recommendation-card"
      data-scope-kind={recommendation.scope_kind || ""}
    >
      <header className={`px-4 pt-3 pb-1.5 flex items-center gap-2 border-b ${
        isMotOnlyTest ? "border-slate-100" : "border-teal-100"
      }`}>
        <div className={`w-1.5 h-1.5 rounded-full ${
          isMotOnlyTest ? "bg-slate-500" : "bg-teal-600"
        }`} />
        <p className={`text-[12px] uppercase tracking-[0.18em] font-semibold ${
          isMotOnlyTest ? "text-slate-600" : "text-teal-700"
        }`}>
          {isMotOnlyTest ? "MOT Test · Workshop Handover" : "Job Card · Workshop Handover"}
        </p>
      </header>

      <div className="px-4 py-4 space-y-3">
        <div>
          <p className="text-[16px] font-bold text-slate-900 leading-snug">
            {label}
          </p>
          <p className="text-[14px] text-slate-600 mt-1.5 leading-snug">
            {recommendation.reason}
          </p>
        </div>

        {isMotOnlyTest && (
          <div
            className="text-[12.5px] text-slate-700 bg-white border border-slate-200 rounded px-3 py-2 leading-snug"
            data-testid="mot-only-scope-note"
          >
            <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
              Scope boundary
            </p>
            <p>
              No Safety &amp; Reliability Inspection. No health check. If the
              tester notices anything outside MOT scope it&apos;s mentioned to
              the customer separately — never billed as part of this booking.
            </p>
          </div>
        )}

        {hereFor && (
          <div className="flex items-center gap-2 text-[13px] text-slate-500">
            <span className="uppercase tracking-wider font-medium">Vehicle here for:</span>
            <Badge variant="outline" className="text-[12px] uppercase tracking-wider bg-white">
              {hereFor.replace("_", " + ")}
            </Badge>
          </div>
        )}

        {(recommendation.escalations || []).length > 0 && (
          <div className="text-[13.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 leading-snug">
            <div className="flex items-center gap-1.5 text-amber-700 mb-1">
              <AlertCircle className="w-3.5 h-3.5" />
              <span className="text-[12px] uppercase tracking-wider font-semibold">Escalated</span>
            </div>
            {recommendation.escalations.map((e, i) => (
              <p key={i} className="mt-0.5">{e}</p>
            ))}
          </div>
        )}

        {/* MOT slot time + expected duration — surfaced INLINE next to the
            CTA so the FOH can't miss them. Wired to the same booking_intent
            endpoint as the lower MOTBookingIntentCard; saves are debounced
            via onBlur (light touch). The Send button is gated on a valid
            slot. */}
        {isMotOnlyTest && !linkedJobId && (
          <div
            className="rounded-md border border-slate-200 bg-white px-3 py-3 space-y-2"
            data-testid="inspection-rec-slot-strip"
          >
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-500">
              <Clock className="w-3 h-3" />
              MOT slot time
              {slotLoading && <Loader2 className="w-3 h-3 ml-1 animate-spin opacity-50" />}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="datetime-local"
                value={slotTime}
                onChange={(e) => setSlotTime(e.target.value)}
                onBlur={() => slotTime && persistSlot({
                  mot_slot_time: new Date(slotTime).toISOString(),
                })}
                // Block past dates at the input level — pick today or
                // later. Tester queue used to silently exclude
                // past-dated MOTs which trapped users in "I built the
                // intake but the tester can't see it" support tickets.
                min={(() => {
                  const d = new Date();
                  const p = (n) => String(n).padStart(2, "0");
                  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T00:00`;
                })()}
                className="col-span-2 text-[13.5px] border border-slate-300 rounded px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400"
                data-testid="inspection-rec-slot-time"
                placeholder="dd/mm/yyyy, --:--"
              />
              <input
                type="number"
                value={duration}
                min={5}
                max={480}
                placeholder="45 min"
                onChange={(e) =>
                  setDuration(e.target.value === "" ? "" : Number(e.target.value))
                }
                onBlur={() => duration && persistSlot({
                  expected_duration_minutes: Number(duration),
                })}
                className="text-[13.5px] border border-slate-300 rounded px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400"
                data-testid="inspection-rec-slot-duration"
              />
            </div>
            <p className="text-[11px] italic text-slate-400 leading-snug">
              Auto-checks DVSA from the slot time; stops {Number(duration) || 45} + 60&thinsp;min later.
            </p>
          </div>
        )}

        {linkedJobId ? (
          <div className="pt-1">
            <Badge
              className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[12.5px]"
              variant="outline"
              data-testid="linked-job-badge"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" />
              Linked to job {linkedJobId.slice(0, 8)}…
            </Badge>
          </div>
        ) : (
          <>
            <Button
              onClick={handoff}
              disabled={!isLocked || submitting}
              title={!isLocked ? "Complete the intake to send to workshop" : ""}
              className={`w-full text-white text-[14px] font-semibold h-11 ${
                isMotOnlyTest
                  ? "bg-slate-800 hover:bg-slate-900"
                  : "bg-teal-700 hover:bg-teal-800"
              }`}
              data-testid="open-inspection-preview-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating MOT job…
                </>
              ) : (
                <>
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  {isLocked
                    ? (isMotOnlyTest ? "Send MOT job to workshop" : "Send to workshop")
                    : "Complete intake to send"}
                  {isLocked && !submitting && <ArrowUpRight className="w-4 h-4 ml-2" />}
                </>
              )}
            </Button>
            {submitError && (
              <p
                className="mt-2 text-[12.5px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-start gap-1.5"
                data-testid="inspection-rec-error"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {submitError}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
